// how-to-start/store/deck-build-lock.ts
//
// THE DECK IS NOT THE USER'S TO EDIT WHILE IT IS BEING WRITTEN.
//
// A deck built with AI streams into the open editor slide by slide, and the
// runner places each one BY POSITION (see ../deck-build-runner). So an edit
// anywhere in the deck while it runs is unsafe, not only an edit to a slide
// still to come:
//
//   - moving or deleting a finished slide shifts every position after it,
//     and the next slide lands in the wrong place;
//   - the blank slide the story opens with is a placeholder the runner
//     removes once it has a real one -- typing into it, or applying a layout
//     from its "Get started with layouts" strip, is lost or worse;
//   - undo walks back the runner's own inserts.
//
// So the whole editor is off limits from the moment the build starts until it
// ends, whichever way it ends. Not an allow-list of safe controls -- every
// control left live turned out to be a way to break the build -- but one
// page-wide shield (editor/components/deck-build-shield): the app root goes
// `inert`, so nothing in it takes a click, a key or focus, and a transparent
// layer covers the page. The only live control is the banner's Stop, which is
// how the user takes the deck back early. Scrolling the page to watch the
// slides arrive still works; the per-component guards below stay as defence
// for the paths that do not go through the pointer (shortcuts, paste, the
// publish event).
//
// Only the document being BUILT is locked. Every standalone version of a
// user's hangs off the same hidden host story, so the story alone does not
// say which document that is; the version has to match too.

import { store, useAppSelector } from "@store";
import { type DeckBuildStatus, useDeckBuildStore } from "./deck-build.store";

/** The build states in which the runner may still write to the deck. */
const WRITING: ReadonlySet<DeckBuildStatus> = new Set<DeckBuildStatus>([
  "planning",
  "building",
]);

type LockInput = {
  status: DeckBuildStatus;
  jobStoryId: string | null | undefined;
  jobVersionId: string | null | undefined;
  openStoryId: string | null | undefined;
  openVersionId: string | null | undefined;
};

/** Whether the open document is the one a running build is writing to. */
export const isDeckBuildLocking = ({
  status,
  jobStoryId,
  jobVersionId,
  openStoryId,
  openVersionId,
}: LockInput): boolean => {
  if (!WRITING.has(status)) return false;
  if (!jobStoryId || jobStoryId !== openStoryId) return false;
  return (jobVersionId ?? null) === (openVersionId ?? null);
};

type OpenDocument = {
  story?: { _id?: string } | null;
  version?: { _id?: string } | null;
};

const openStoryIdOf = (story: OpenDocument | undefined) =>
  story?.story?._id ?? null;
const openVersionIdOf = (story: OpenDocument | undefined) =>
  story?.version?._id ?? null;

/**
 * The lock, for components: re-renders when the build starts or ends.
 *
 * Only the fields the lock reads, one primitive each -- NOT
 * useDeckBuildProgress, whose `placed` changes with every slide and would
 * re-render every thumbnail of the deck each time one lands.
 */
export const useDeckBuildLock = (): boolean => {
  const status = useDeckBuildStore((s) => s.status);
  const storyId = useDeckBuildStore((s) => s.job?.storyId ?? null);
  const versionId = useDeckBuildStore((s) => s.job?.versionId ?? null);
  const openStoryId = useAppSelector((state) =>
    openStoryIdOf(state.story as OpenDocument),
  );
  const openVersionId = useAppSelector((state) =>
    openVersionIdOf(state.story as OpenDocument),
  );
  return isDeckBuildLocking({
    status,
    jobStoryId: storyId,
    jobVersionId: versionId,
    openStoryId,
    openVersionId,
  });
};

/**
 * The same answer outside React -- for the document-level keyboard and paste
 * handlers, which read the current state when a key is pressed.
 */
export const isDeckBuildLockedNow = (): boolean => {
  const { status, job } = useDeckBuildStore.getState();
  const story = store.getState().story as OpenDocument;
  return isDeckBuildLocking({
    status,
    jobStoryId: job?.storyId,
    jobVersionId: job?.versionId,
    openStoryId: openStoryIdOf(story),
    openVersionId: openVersionIdOf(story),
  });
};
